import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const guideLinks = [
  { name: 'Guida Completa 2026', slug: '/guida-itaobuy-sheet-2026' },
  { name: 'Sicurezza Acquisti', slug: '/guida-sicurezza-itaobuy' },
  { name: 'Guida per Principianti', slug: '/come-usare-itaobuy-principianti' },
  { name: 'Strategie di Ricerca', slug: '/strategie-ricerca-prodotti' },
  { name: 'Errori Comuni', slug: '/errori-comuni-itaobuy' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeAll = () => {
    setDropdownOpen(false);
    setGuideOpen(false);
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            onClick={closeAll}
            className="text-foreground-900 font-heading text-xl md:text-2xl font-bold whitespace-nowrap cursor-pointer hover:text-accent-600 transition-colors"
          >
            Itaobuy Spreadsheet Italia
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-foreground-700 text-sm font-medium hover:text-accent-600 hover:bg-accent-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1"></i> Home
            </Link>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => {
                  setGuideOpen(!guideOpen);
                  setDropdownOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 bg-background-50 text-foreground-700 text-sm font-medium hover:border-accent-400 hover:text-accent-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line mr-1"></i> Guida agli Acquisti
                {guideOpen ? (
                  <i className="ri-arrow-up-s-line text-sm transition-transform"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-sm transition-transform"></i>
                )}
              </button>

              {guideOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200 rounded-2xl shadow-lg p-3 w-[240px] z-50">
                  {guideLinks.map((link) => (
                    <Link
                      key={link.slug}
                      to={link.slug}
                      onClick={() => setGuideOpen(false)}
                      className="block px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => {
                  setDropdownOpen(!dropdownOpen);
                  setGuideOpen(false);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-300 bg-background-50 text-foreground-700 text-sm font-medium hover:border-accent-400 hover:text-accent-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <span>{t('nav.categories')}</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-sm transition-transform"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-sm transition-transform"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 bg-background-50 border border-background-200 rounded-2xl shadow-lg p-4 w-[520px] z-50">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="px-3 py-2 text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('cta.itaobuy_sheet')}
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('cta.buy_now')}
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-5 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              onClick={closeAll}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-accent-50 text-accent-700 text-sm font-semibold cursor-pointer"
            >
              <i className="ri-home-4-line"></i> Home
            </Link>

            <div>
              <p className="text-xs text-foreground-500 font-semibold uppercase tracking-wider mb-2">
                <i className="ri-book-open-line mr-1"></i> Guida agli Acquisti
              </p>
              <div className="space-y-1">
                {guideLinks.map((link) => (
                  <Link
                    key={link.slug}
                    to={link.slug}
                    onClick={closeAll}
                    className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-foreground-500 font-semibold uppercase tracking-wider mb-2">
                <i className="ri-price-tag-3-line mr-1"></i> {t('nav.categories')}
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="px-2 py-2 text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-3 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 text-center px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                {t('cta.itaobuy_sheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 text-center px-4 py-2.5 rounded-full border-2 border-primary-500 text-primary-500 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                {t('cta.buy_now')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}